// src/zaplati/index.js — екран „Заплати" (ПРОМТ-17): качваш MEMORD1.XML от
// РЕГАТА, виждаш какво ще съдържа файлът за Конто 6 (с партидите на фирмата),
// генерираш го и го сваляш. Отстрани — „Настройки на колоните": таблицата
// позиция → партиди, същата като листа „Аналитичност" в Excel-а.
//
// Потребителят е счетоводител без технически опит: всяка стъпка е с едри
// думи, грешките казват КАКВО да се направи, нищо не се гадае тихо.

import { el, clear, field, input } from '../shared/dom.js';
import { api } from '../shared/api.js';
import { toast } from '../shared/toast.js';
import { state, isAdminUser } from '../shared/state.js';
import { renderShell } from '../shell/shell.js';
import { emptyState } from '../shared/ui.js';
import { dropZone } from '../shared/upload.js';

const MONTHS = ['', 'януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
const fmtEu = (n) => (Math.round(n * 100) / 100).toFixed(2);

export function view(page) {
  const st = state();
  if (!st.activeCompany) {
    page.appendChild(emptyState('₴', 'Изберете фирма от менюто горе, за да работите със заплатите ѝ.'));
    return;
  }
  const companyId = st.activeCompany;
  const cq = '?company_id=' + companyId;

  let settings = { items: {} };
  let lastFile = null;          // избраният XML (File) — пази се за „Генерирай"
  let lastPreview = null;

  const main = el('div', { style: 'flex:1 1 560px;min-width:0' });
  const side = el('div', { style: 'flex:0 1 460px;min-width:320px' });
  page.appendChild(el('div', { class: 'zaplati', style: 'display:flex;gap:20px;flex-wrap:wrap;align-items:flex-start' }, main, side));

  // ---- Качването -----------------------------------------------------------

  // Зоната е общата с „Доставки" (стъпка 8): преди тук стоеше голото поле на
  // браузъра — „Choose File" на английски и без пускане с мишката.
  const zone = dropZone({
    id: 'zaplati-file-input', accept: '.xml,text/xml',
    title: 'Пусни справката от РЕГАТА тук', ariaLabel: 'Качване на справката от РЕГАТА',
    hint: 'Файлът MEMORD1.XML от папката C:\\KSoft\\zaplati.',
    onFiles: (files) => onPick(files && files[0]),
  });
  const resultBox = el('div');
  const historyBox = el('div');

  main.appendChild(el('div', { class: 'card', style: 'padding:16px;margin-bottom:16px' },
    el('h2', { text: 'Заплати → файл за Конто 6' }),
    // Пътят в РЕГАТА — номерирани стъпки, не един дълъг ред: човек го
    // следва с програмата отворена до този екран.
    el('ol', { class: 'steps-list' },
      el('li', { text: 'В РЕГАТА: Заплати → т. 7 „Счетоводна справка“.' }),
      el('li', { text: 'Изберете фирмата и месеца → „2. По сметки“.' }),
      el('li', { text: 'Пуснете тук получения файл C:\\KSoft\\zaplati\\MEMORD1.XML (или натиснете „Избери файл“).' })),
    zone,
    resultBox));
  main.appendChild(historyBox);

  async function onPick(f) {
    if (!f) return;
    // Удобство, без заявка за очевидно чужд файл; истинската проверка е на сървъра.
    if (!/\.xml$/i.test(f.name || '')) { toast('Изберете справката MEMORD1.XML от РЕГАТА (XML файл) — „' + f.name + '“ не е такъв.', true); return; }
    lastFile = f;
    await runConvert('/zaplati/preview', f);
  }

  async function runConvert(path, f, after) {
    clear(resultBox);
    resultBox.appendChild(el('p', { class: 'muted', text: 'Четене на справката…' }));
    let r;
    try {
      r = await api(path + cq, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'X-File-Modified': String(f.lastModified || 0) },
        body: f,
      });
    } catch (e) {
      clear(resultBox);
      if (e.status === 409) {
        // api() носи само текста на грешката; за бутона „Премини във …"
        // трябват и данните от отговора — второ, сурово питане (рядък случай).
        const raw = await previewRaw(f);
        resultBox.appendChild(alertBox('red', raw.body.error || e.message, raw.body.switch_company));
      } else {
        resultBox.appendChild(alertBox('red', e.message));
      }
      return;
    }
    lastPreview = r;
    renderResult(r, after);
  }

  // При 409 api() губи допълнителните полета — затова прегледът пита сам
  // сървъра и показва бутона „Премини във фирмата от файла", когато го има.
  async function previewRaw(f) {
    const res = await fetch('/api/zaplati/preview' + cq, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/octet-stream', 'X-File-Modified': String(f.lastModified || 0) },
      body: f,
    });
    return { status: res.status, body: await res.json().catch(() => ({})) };
  }

  function alertBox(color, text, switchCompany) {
    const box = el('div', { class: 'alert ' + color, role: 'alert', style: 'margin-top:12px' }, el('div', { text }));
    if (switchCompany) {
      box.appendChild(el('button', {
        class: 'btn sm', style: 'margin-top:8px', text: 'Премини във „' + switchCompany.name + '"',
        onClick: () => {
          const s = state();
          s.activeCompany = switchCompany.id;
          localStorage.setItem('activeCompany', switchCompany.id);
          renderShell();
        },
      }));
    }
    return box;
  }

  function renderResult(r, after) {
    clear(resultBox);

    // Едрото потвърждение: коя фирма, кой месец, колко записа — човекът
    // първо СВЕРЯВА с очи, после гледа редовете (XML-ът се презаписва лесно).
    resultBox.appendChild(el('div', { style: 'margin:12px 0;padding:12px;border:1px solid var(--border);border-radius:8px' },
      el('div', { style: 'font-size:20px;font-weight:600', text: r.xml_firma + ' · ' + MONTHS[r.mes] + ' ' + r.god }),
      el('div', { class: 'muted', text: r.records + ' записа в справката · файл ' + r.filename })));

    for (const w of r.warnings || []) resultBox.appendChild(alertBox('amber', w));
    if (r.stops && r.stops.length) {
      for (const s of r.stops) resultBox.appendChild(alertBox('red', s));
      resultBox.appendChild(el('p', { class: 'muted', text: 'Файл не се генерира, докато горното не се изчисти — Конто би го отказало целия.' }));
      return;
    }
    for (const n of r.notes || []) resultBox.appendChild(alertBox('blue', n));

    // Редовете — каквото ще съдържа файлът, ред по ред.
    const tbl = el('table', { class: 'table', style: 'margin-top:12px' },
      el('thead', {}, el('tr', {},
        el('th', { text: 'Дата' }), el('th', { text: 'Дт' }), el('th', { text: 'Дт партида' }),
        el('th', { text: 'Кт' }), el('th', { text: 'Кт партида' }), el('th', { text: 'Сума', style: 'text-align:right' }),
        el('th', { text: 'Позиция' }), el('th', { text: 'Текст' }))),
      el('tbody', {}, ...r.rows.map((x) => el('tr', {},
        el('td', { text: x.date }), el('td', { text: x.dt }), el('td', { text: x.dtp || '—' }),
        el('td', { text: x.kt }), el('td', { text: x.ktp || '—' }),
        el('td', { text: fmtEu(Number(x.suma)), style: 'text-align:right;white-space:nowrap' }),
        el('td', { text: x.item }), el('td', { text: x.text })))));
    resultBox.appendChild(tbl);

    // Сверката от т. 7.2 — числата, които се гледат срещу ведомостта и Д6.
    const t = r.totals;
    const pair = (label, obj) => el('div', {},
      el('b', { text: label }),
      el('div', {}, ...Object.entries(obj).map(([k, v]) => el('div', { class: 'muted', text: k + ': ' + fmtEu(v) + ' €' }))));
    resultBox.appendChild(el('div', { style: 'display:flex;gap:28px;flex-wrap:wrap;margin:14px 0;padding:12px;border:1px solid var(--border);border-radius:8px' },
      pair('По дебит', t.byDt), pair('По кредит', t.byKt),
      pair('455 по партиди (срещу Д6)', t.k455),
      el('div', {}, el('b', { text: 'Общо' }), el('div', { class: 'muted', text: t.rows + ' реда · ' + fmtEu(t.total) + ' €' }))));

    if (after) { after(r); return; }

    const sameMonth = (historyRuns || []).filter((x) => x.mes === r.mes && x.god === r.god);
    if (sameMonth.length) {
      resultBox.appendChild(alertBox('amber', 'За ' + MONTHS[r.mes] + ' ' + r.god + ' вече е генериран файл на ' + fmtWhen(sameMonth[0].created_at) + ' от ' + sameMonth[0].created_by_name + '. Новият НЕ изтрива стария — и двата остават в историята.'));
    }
    resultBox.appendChild(el('button', { class: 'btn', style: 'margin-top:8px', text: 'Генерирай и свали файла', onClick: generate }));

    mergePreviewIntoSettings(r);
  }

  async function generate() {
    if (!lastFile) return;
    try {
      const r = await api('/zaplati/generate' + cq, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'X-File-Modified': String(lastFile.lastModified || 0) },
        body: lastFile,
      });
      toast('Файлът е генериран.');
      await download(r.run_id, r.filename);
      await loadHistory();
      renderResult(r, () => {
        resultBox.appendChild(alertBox('green', 'Готово: ' + r.filename + ' е свален. В Конто 6: Администриране → Импорт на работни заплати (р-л 7.9.8).'));
      });
    } catch (e) {
      toast(e.message);
    }
  }

  async function download(runId, filename) {
    const res = await fetch('/api/zaplati/runs/' + runId + '/file', { credentials: 'same-origin' });
    if (!res.ok) { toast('Файлът не можа да се свали.'); return; }
    const blob = await res.blob();
    const a = el('a', { href: URL.createObjectURL(blob), download: filename });
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // ---- Историята -----------------------------------------------------------

  let historyRuns = [];
  function fmtWhen(iso) {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  async function loadHistory() {
    try { historyRuns = await api('/zaplati/runs' + cq); } catch (e) { historyRuns = []; }
    clear(historyBox);
    if (!historyRuns.length) return;
    historyBox.appendChild(el('div', { class: 'card', style: 'padding:16px' },
      el('h3', { text: 'Генерирани файлове' }),
      el('table', { class: 'table' },
        el('thead', {}, el('tr', {}, el('th', { text: 'Месец' }), el('th', { text: 'Файл' }), el('th', { text: 'Кога · от кого' }), el('th', { text: 'Общо' }), el('th', { text: '' }))),
        el('tbody', {}, ...historyRuns.map((r) => el('tr', {},
          el('td', { text: MONTHS[r.mes] + ' ' + r.god }),
          el('td', { text: r.filename }),
          el('td', { text: fmtWhen(r.created_at) + ' · ' + (r.created_by_name || '') }),
          el('td', { text: r.totals ? fmtEu(r.totals.total) + ' €' : '' }),
          el('td', {}, el('button', { class: 'btn sm ghost', text: 'Свали пак', 'aria-label': 'Свали пак ' + r.filename, onClick: () => download(r.id, r.filename) }))))))));
  }

  // ---- „Настройки на колоните" (аналитичността) ----------------------------
  //
  // Панелът е СГЪВАЕМ: със запазена аналитичност стои прибран на един ред,
  // за да не тежи на екрана; нова фирма без настройки го вижда отворен, а
  // нова позиция от справката също го отваря сама.

  const settingsBody = el('div', { id: 'zaplati-settings-body' });
  let sideOpen = null;   // null = по подразбиране: отворен само без запазени настройки
  const sideBtn = el('button', {
    class: 'btn sm ghost', type: 'button', 'aria-controls': 'zaplati-settings-body', 'aria-expanded': 'false',
    onClick: () => setSide(!isSideOpen()),
  });
  function isSideOpen() {
    return sideOpen === null ? !Object.keys(settings.items || {}).length : sideOpen;
  }
  function applySide() {
    const open = isSideOpen();
    settingsBody.style.display = open ? '' : 'none';
    sideBtn.textContent = open ? 'Скрий ▴' : 'Покажи ▾';
    sideBtn.setAttribute('aria-expanded', String(open));
  }
  function setSide(open) { sideOpen = open; applySide(); }

  side.appendChild(el('div', { class: 'card', style: 'padding:16px' },
    el('div', { style: 'display:flex;align-items:center;justify-content:space-between;gap:8px' },
      el('h3', { text: 'Настройки на колоните', style: 'margin:0' }), sideBtn),
    settingsBody));
  applySide();

  function sideInput(item, k, val, label) {
    return input({ value: val || '', 'data-item': item, 'data-k': k, 'aria-label': label + ' за позиция ' + item });
  }

  function renderSettings() {
    clear(settingsBody);
    settingsBody.appendChild(el('p', { class: 'muted', text: 'Партидите на фирмата по позиции — както в Конто. Партидата е текст: „01#" не е „1#", а „#" се пише и накрая. Празно = сметка без аналитичност (нормално за 421).' }));
    settingsBody.appendChild(kontoBox);
    const items = settings.items || {};
    const keys = Object.keys(items).sort();
    if (!keys.length) {
      settingsBody.appendChild(el('p', { class: 'muted', text: 'Още няма позиции. Натиснете „Попълни от Конто" — партидите идват от номенклатурите на фирмата в Конто. Друг начин: Качете първата справка — позициите от нея ще се появят тук с предложени партиди.' }));
      applySide();
      return;
    }
    // Твърди колони (table-layout: fixed, .zaplati-side-table): в тесния
    // панел таблица със свободни ширини преливаше текст върху съседните
    // редове. Текстът на статията стои ПОД номера, не в своя колона — така
    // колоните са 6 и всичко се чете; текстът се пази и не се губи при запис.
    const tbl = el('table', { class: 'table zaplati-side-table' },
      el('colgroup', {},
        el('col', { style: 'width:116px' }), el('col', { style: 'width:38px' }), el('col', {}),
        el('col', { style: 'width:38px' }), el('col', {}), el('col', { style: 'width:30px' })),
      el('thead', {}, el('tr', {},
        el('th', { text: 'Позиция' }),
        el('th', { text: 'Дт' }), el('th', { text: 'Дт партида' }),
        el('th', { text: 'Кт' }), el('th', { text: 'Кт партида' }), el('th', { text: '✓', title: 'Потвърдено' }))),
      el('tbody', {}, ...keys.map((item) => {
        const v = items[item];
        return el('tr', {},
          el('td', {}, el('div', { text: item }), el('div', { class: 'zaplati-pos-text', text: v.txt || '' })),
          el('td', { class: 'muted', text: v.dt || '' }),
          el('td', {}, sideInput(item, 'dtp', v.dtp, 'Дебит партида')),
          el('td', { class: 'muted', text: v.kt || '' }),
          el('td', {}, sideInput(item, 'ktp', v.ktp, 'Кредит партида')),
          el('td', {}, input({ type: 'checkbox', checked: v.ok === true, 'data-item': item, 'data-k': 'ok', 'aria-label': 'Потвърдено за позиция ' + item })));
      })));
    settingsBody.appendChild(tbl);
    settingsBody.appendChild(el('button', { class: 'btn', style: 'margin-top:10px', text: 'Запази настройките', onClick: saveSettings }));
    applySide();
  }

  // ---- „Попълни от Конто" ---------------------------------------------------
  //
  // Партидите от номенклатурите на фирмата в Конто (services/zaplatiKonto.js).
  // Първо преглед — какво ще се попълни, какво се различава, какво не стана
  // и защо; записът е отделен бутон. Различното се сменя само с отметка.

  const kontoBox = el('div', { class: 'zaplati-konto', style: 'margin:8px 0 12px' });
  const STATUS = {
    filled: 'ще се попълни', replaced: 'ще се смени', same: 'същото', differ: 'различно в Конто',
    problem: 'не е намерено', manual: 'попълва се на ръка',
  };
  function renderKontoIdle() {
    clear(kontoBox);
    kontoBox.appendChild(el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' },
      el('button', { class: 'btn sm', type: 'button', text: 'Попълни от Конто', onClick: () => kontoPreview(false) }),
      isAdminUser() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Всички фирми…', onClick: allPreview }) : null));
    kontoBox.appendChild(el('p', { class: 'muted', style: 'font-size:12px;margin:6px 0 0', text: 'Взима партидите от номенклатурите на тази фирма в Конто (вид дейност, статии на 604/605, видове вноски по 455, данъци по 454). Попълва празното; нищо не се записва без „Запиши".' }));
  }

  async function kontoPreview(replace) {
    let r;
    try { r = await api('/zaplati/konto-fill' + cq + (replace ? '&replace=1' : '')); } catch (e) { toast(e.message, true); return; }
    clear(kontoBox);
    const c = r.count;
    kontoBox.appendChild(el('div', { role: 'status', style: 'padding:8px;border:1px solid var(--border);border-radius:8px' },
      el('b', { text: 'От Конто: ' + c.filled + ' за попълване' + (replace ? ', ' + c.replaced + ' за смяна' : '') + ', ' + c.same + ' съвпадат' + (c.differ ? ', ' + c.differ + ' различни' : '') + (c.problem ? ', ' + c.problem + ' не са намерени' : '') + '.' }),
      r.imported_at ? el('div', { class: 'muted', style: 'font-size:12px', text: 'Данни от Конто, внесени на ' + new Date(r.imported_at).toLocaleDateString('bg-BG') + '.' }) : null));
    // Важното отгоре: различното и ненамереното; попълваното — в сгъваем
    // списък (в тесния панел 27 реда скриваха бутоните).
    const pick = (st) => {
      const out = [];
      for (const row of r.rows) for (const sd of row.sides) if (st.includes(sd.status)) out.push([row, sd]);
      return out;
    };
    const table = (list) => el('table', { class: 'table zaplati-konto-table', style: 'margin-top:6px;font-size:12px' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Позиция' }), el('th', { text: 'Сега → от Конто' }), el('th', { text: 'Защо' }))),
      el('tbody', {}, ...list.map(([row, sd]) => el('tr', {},
        el('td', {}, el('div', { text: row.item + ' · ' + sd.side + ' ' + sd.account }), el('div', { class: 'zaplati-pos-text', text: row.txt || '' })),
        el('td', { style: 'white-space:nowrap', text: (sd.was || '—') + ' → ' + ((sd.status === 'problem' ? '' : (sd.status === 'differ' ? sd.konto : sd.now)) || '—') }),
        el('td', {}, el('div', { text: STATUS[sd.status] }), el('div', { class: 'muted', text: sd.problem || sd.why || '' }))))));
    const differ = pick(['differ', 'replaced']);
    const problem = pick(['problem']);
    const filled = pick(['filled']);
    if (differ.length) kontoBox.appendChild(el('div', { style: 'margin-top:8px' }, el('b', { text: replace ? 'Ще се сменят' : 'Различни от сегашните' }), table(differ)));
    if (problem.length) kontoBox.appendChild(el('div', { style: 'margin-top:8px' }, el('b', { text: 'Не са намерени в Конто' }), table(problem)));
    if (filled.length) kontoBox.appendChild(el('details', { style: 'margin-top:8px' }, el('summary', { text: 'Ще се попълнят: ' + filled.length + ' — покажи' }), table(filled)));
    const repl = c.differ || replace
      ? input({ type: 'checkbox', id: 'zaplati-konto-replace', checked: !!replace, onChange: (e) => kontoPreview(e.target.checked) })
      : null;
    kontoBox.appendChild(el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:8px' },
      el('button', { class: 'btn sm', type: 'button', text: 'Запиши', disabled: !(c.filled || (replace && c.replaced)), onClick: () => kontoApply(replace) }),
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Откажи', onClick: renderKontoIdle }),
      repl ? el('label', { for: 'zaplati-konto-replace', style: 'display:flex;gap:4px;align-items:center' }, repl, el('span', { text: 'Замени и различните (и потвърдените)' })) : null));
  }

  async function kontoApply(replace) {
    let r;
    try { r = await api('/zaplati/konto-fill' + cq + (replace ? '&replace=1' : ''), { method: 'POST', body: '{}' }); } catch (e) { toast(e.message, true); return; }
    settings = r.settings || settings;
    toast('Записано: ' + (r.count.filled + (replace ? r.count.replaced : 0)) + ' партиди от Конто. Прегледайте ги и сложете ✓.');
    renderKontoIdle();
    setSide(true);
    renderSettings();
    if (lastFile) runConvert('/zaplati/preview', lastFile);
  }

  // Всички фирми — само админ. Попълва САМО празното; различното се гледа
  // фирма по фирма („Попълни от Конто" във всяка).
  async function allPreview() {
    let r;
    try { r = await api('/zaplati/konto-fill-all', { method: 'POST', body: '{}' }); } catch (e) { toast(e.message, true); return; }
    clear(kontoBox);
    const total = r.firms.reduce((t, f) => t + f.filled, 0);
    kontoBox.appendChild(el('div', { role: 'status' },
      el('b', { text: 'Всички фирми: ' + total + ' партиди за попълване в ' + r.firms.filter((f) => f.filled).length + ' фирми.' }),
      r.without_data.length ? el('div', { class: 'muted', style: 'font-size:12px', text: 'Без данни от Конто (' + r.without_data.length + '): ' + r.without_data.map((f) => f.name).join(', ') + '.' }) : null));
    kontoBox.appendChild(el('table', { class: 'table', style: 'margin-top:8px;font-size:12px' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Фирма' }), el('th', { text: 'Попълва' }), el('th', { text: 'Различни' }), el('th', { text: 'Не са намерени' }))),
      el('tbody', {}, ...r.firms.map((f) => el('tr', {},
        el('td', { text: f.name }), el('td', { text: String(f.filled) }), el('td', { text: String(f.differ) }), el('td', { text: String(f.problem) }))))));
    kontoBox.appendChild(el('div', { style: 'display:flex;gap:8px;margin-top:8px' },
      el('button', { class: 'btn sm', type: 'button', text: 'Попълни празните във всички', disabled: !total, onClick: async () => {
        try { await api('/zaplati/konto-fill-all?mode=apply', { method: 'POST', body: '{}' }); } catch (e) { toast(e.message, true); return; }
        toast('Готово: попълнени ' + total + ' партиди. Различните прегледайте фирма по фирма.');
        try { settings = await api('/zaplati/settings' + cq); } catch (e) { /* остава старото */ }
        renderKontoIdle();
        renderSettings();
      } }),
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Откажи', onClick: renderKontoIdle })));
  }
  renderKontoIdle();

  async function saveSettings() {
    const items = JSON.parse(JSON.stringify(settings.items || {}));
    settingsBody.querySelectorAll('input[data-item]').forEach((inp) => {
      const it = items[inp.dataset.item] = items[inp.dataset.item] || {};
      if (inp.dataset.k === 'ok') it.ok = inp.checked;
      else it[inp.dataset.k] = inp.value.trim();
    });
    try {
      settings = await api('/zaplati/settings' + cq, { method: 'PUT', body: JSON.stringify({ items }) });
      toast('Настройките са записани.');
      // Записаното прибира панела („веднъж запазено — менюто се сгъва").
      setSide(false);
      renderSettings();
      // Партидите се промениха — ако има качен файл, прегледът се преизчислява.
      if (lastFile) runConvert('/zaplati/preview', lastFile);
    } catch (e) { toast(e.message); }
  }

  // Нова позиция от прегледа влиза в таблицата НЕпотвърдена, с предложените
  // партиди — така човекът я вижда и дооправя, вместо тя да се носи само в
  // бележките. Нищо не се записва без „Запази настройките".
  function mergePreviewIntoSettings(r) {
    const items = settings.items || (settings.items = {});
    let added = false;
    for (const x of r.rows) {
      if (items[x.item]) continue;
      items[x.item] = { dt: x.dt, dtp: x.dtp, kt: x.kt, ktp: x.ktp, txt: x.text.replace(/ м\.\d{2}\/\d{4}$/, ''), ok: false };
      added = true;
    }
    if (added) { setSide(true); renderSettings(); }
  }

  // ---- Старт ---------------------------------------------------------------

  (async () => {
    try { settings = await api('/zaplati/settings' + cq); } catch (e) { settings = { items: {} }; }
    renderSettings();
    loadHistory();
  })();
}
